/**
 =========================================================
 * Material Dashboard 2 PRO React - v2.1.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import React, {useEffect, useState} from "react";
// react-router components
import {Navigate, Route, Routes, useLocation} from "react-router-dom";
// @mui material components
import {ThemeProvider} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
// Material Dashboard 2 PRO React components
// Material Dashboard 2 PRO React examples
import Sidenav from "components/Sidenav";
// Material Dashboard 2 PRO React themes
import theme from "assets/theme";
// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";
import {setMiniSidenav, useMaterialUIController} from "context";
// Images
import brandWhite from "assets/images/logo.png";
import brandDark from "assets/images/logo.png";
import brandMini from "assets/images/logo_mini.png";
import {useDispatch, useSelector} from "react-redux";
import {signIn} from "./store/actions/auth";
import axios from "axios";
import {API_URL} from "./config";
import Loading from "./layouts/loading";
import {adminRoutes, operatorRoutes, managerRoutes} from "./routes";
import RecoverPassword from "./layouts/authentication/recover-password/RecoverPassword";
import ResetPassword from "./layouts/authentication/reset-password/ResetPassword";
import {SnackbarProvider} from "./context/SnackbarContext/SnackbarContext";
import SignIn from "./layouts/authentication/sign-in/SignIn";
import NewLocation from "./layouts/locations/new-location/NewLocation";
import EditLocation from "./layouts/locations/edit-locations/EditLocation";
import NewProduct from "./layouts/products/new-product/NewProduct";
import EditProduct from "./layouts/products/edit-product/EditProduct";
import NewDoctor from "./layouts/doctors/new-doctor/NewDoctor";
import EditDoctor from "./layouts/doctors/edit-doctor/EditDoctor";
import DoctorDetails from "./layouts/doctors/details/DoctorDetails";
import NewUser from "./layouts/users/new-user/NewUser";
import EditUser from "./layouts/users/edit-user/EditUser";
import NewSale from "./layouts/sales/new-sale/NewSale";
import NewPatient from "./layouts/patients/new-patient/NewPatient";
import SaleDetails from "./layouts/sales/details/SaleDetails";
import EditPatient from "./layouts/patients/edit-patient/EditPatient";
import PayoutDetails from "./layouts/payouts/details/PayoutDetails";
import EditPayout from "./layouts/payouts/edit-payout/EditPayout";
import PatientDetails from "./layouts/patients/details/PatientDetails";

export default function App() {
    const [controller, materialDispatch] = useMaterialUIController();

    const {
        miniSidenav,
        direction,
        layout,
        openConfigurator,
        sidenavColor,
        transparentSidenav,
        whiteSidenav,
        darkMode,
    } = controller;
    const [onMouseEnter, setOnMouseEnter] = useState(false);
    const {pathname} = useLocation();


    const dispatch = useDispatch();

    const authState = useSelector((state) => state.authReducer);

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {

        const checkUser = async () => {
            setIsLoading(true);

            try {
                const response = await axios.get(`${API_URL}/user`);

                dispatch(signIn(response.data.data.user.id, response.data.data.user.name, response.data.data.role.id, response.data.data.location?.id));

            } catch (error) {

            }

            setIsLoading(false);
        };

        checkUser();

    }, []);

    // Open sidenav when mouse enter on mini sidenav
    const handleOnMouseEnter = () => {
        if (miniSidenav && !onMouseEnter) {
            setMiniSidenav(materialDispatch, false);
            setOnMouseEnter(true);
        }
    };

    // Close sidenav when mouse leave mini sidenav
    const handleOnMouseLeave = () => {
        if (onMouseEnter) {
            setMiniSidenav(materialDispatch, true);
            setOnMouseEnter(false);
        }
    };

    // Setting page scroll to 0 when changing the route
    useEffect(() => {
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
    }, [pathname]);

    const getRoutes = (allRoutes) =>
        allRoutes.map((route) => {
            if (route.collapse) {
                return getRoutes(route.collapse);
            }

            if (route.route) {
                return <Route exact path={route.route} element={route.component} key={route.key}/>;
            }

            return null;
        });

    const getRoleRoutes = (roleId) => {
        switch (roleId) {
            case 1:
                return adminRoutes;
            case 2:
                return managerRoutes;
            case 3:
                return operatorRoutes;
        }
    };

    return <ThemeProvider theme={theme}>
        <SnackbarProvider>
            <CssBaseline/>
            {
                isLoading ?
                    <Loading/> :
                    <>
                        {
                            authState.isLoggedIn ?
                                <>
                                {pathname !== "/ventas/nueva-venta" &&
                                    <Sidenav
                                        color={sidenavColor}
                                        brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                                        brandMini={brandMini}
                                        brandName=""
                                        routes={getRoleRoutes(authState.roleId)}
                                        onMouseEnter={handleOnMouseEnter}
                                        onMouseLeave={handleOnMouseLeave}
                                    />}
                                    {
                                        authState.roleId === 1 &&
                                        <Routes>
                                            {getRoutes(adminRoutes)}
                                            <Route exact path="/usuarios/nuevo-usuario" element={<NewUser />}
                                                   key="nuevo-usuario"/>
                                            <Route exact path="/usuarios/:id/editar-usuario" element={<EditUser />}
                                                   key="editar-usuario"/>
                                            <Route exact path="/sucursales/nueva-sucursal" element={<NewLocation />}
                                                   key="nueva-sucursal"/>
                                            <Route exact path="/sucursales/:id/editar-sucursal" element={<EditLocation />}
                                                   key="editar-sucursal"/>
                                            <Route exact path="/productos/nuevo-producto" element={<NewProduct />}
                                                   key="nuevo-producto"/>
                                            <Route exact path="/productos/:id/editar-producto" element={<EditProduct />}
                                                   key="editar-producto"/>
                                            <Route exact path="/doctores/nuevo-doctor" element={<NewDoctor />}
                                                   key="nuevo-doctor"/>
                                            <Route exact path="/doctores/:id/editar-doctor" element={<EditDoctor />}
                                                   key="editar-doctor"/>
                                            <Route exact path="/doctores/:id" element={<DoctorDetails />}
                                                   key="detalles-doctor"/>
                                            <Route exact path="/ventas/:id" element={<SaleDetails />}
                                                   key="detalles-venta"/>
                                            <Route exact path="/pacientes/nuevo-paciente" element={<NewPatient/>}
                                                   key="nuevo-paciente"/>
                                            <Route exact path="/pacientes/:id/editar-paciente" element={<EditPatient/>}
                                                   key="editar-paciente"/>
                                            <Route exact path="/pacientes/:id" element={<PatientDetails />}
                                                   key="detalles-paciente"/>
                                            <Route exact path="/pagos/:id/editar-pago" element={<EditPayout/>}
                                                   key="editar-pago"/>
                                            <Route exact path="/pagos/:id" element={<PayoutDetails />}
                                                   key="detalles-pago"/>
                                            <Route path="*" element={<Navigate to="/dashboard"/>}/>
                                        </Routes>
                                    }
                                    {
                                        authState.roleId === 2 &&
                                        <Routes>
                                            {getRoutes(managerRoutes)}
                                            <Route exact path="/ventas/nueva-venta" element={<NewSale />}
                                                   key="nueva-venta"/>
                                            <Route exact path="/pacientes/nuevo-paciente" element={<NewPatient/>}
                                                   key="nuevo-paciente"/>
                                            <Route exact path="/pacientes/:id/editar-paciente" element={<EditPatient />}
                                                   key="editar-paciente"/>
                                            <Route exact path="/ventas/:id" element={<SaleDetails />}
                                                   key="detalles-venta"/>
                                            <Route exact path="/productos/nuevo-producto" element={<NewProduct />}
                                                   key="nuevo-producto"/>
                                            <Route exact path="/productos/:id/editar-producto" element={<EditProduct />}
                                                   key="editar-producto"/>
                                            <Route exact path="/doctores/nuevo-doctor" element={<NewDoctor />}
                                                   key="nuevo-doctor"/>
                                            <Route exact path="/doctores/:id/editar-doctor" element={<EditDoctor />}
                                                   key="editar-doctor"/>
                                            <Route path="*" element={<Navigate to="/pacientes"/>}/>
                                        </Routes>
                                    }
                                    {
                                        authState.roleId === 3 &&
                                        <Routes>
                                            {getRoutes(operatorRoutes)}
                                            <Route exact path="/ventas/nueva-venta" element={<NewSale />}
                                                   key="nueva-venta"/>
                                            <Route exact path="/pacientes/nuevo-paciente" element={<NewPatient/>}
                                                   key="nuevo-paciente"/>
                                            <Route exact path="/pacientes/:id/editar-paciente" element={<EditPatient />}
                                                   key="editar-paciente"/>
                                            <Route exact path="/ventas/:id" element={<SaleDetails />}
                                                   key="detalles-venta"/>
                                            <Route path="*" element={<Navigate to="/ventas/nueva-venta"/>}/>
                                        </Routes>
                                    }
                                </> :
                                <Routes>
                                    <Route exact path="/autenticacion/inicio" element={<SignIn/>}
                                           key="inicio"/>
                                    <Route exact path="/autenticacion/recuperar" element={<RecoverPassword/>}
                                           key="recuperar"/>
                                    <Route exact path="/autenticacion/restablecer" element={<ResetPassword/>}
                                           key="restablecer"/>
                                    <Route exact path="/doctores/:id" element={<DoctorDetails />}
                                           key="detalles-doctor"/>
                                    <Route path="*" element={<Navigate to="/autenticacion/inicio"/>}/>
                                </Routes>
                        }
                    </>
            }
        </SnackbarProvider>
    </ThemeProvider>
}
